import { useInfiniteQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo } from 'react';

import { CenterFlex, VFlex } from '@/components/base';
import List from '@/components/List/List';
import Typo from '@/components/Typography/Typo';
import AMP_KEYS from '@/configs/amp-track-keys';
import queryKeys from '@/configs/react-query-keys';
import useAppInit from '@/hooks/domain/App/useAppInit';
import service from '@/services/index';
import useGlobalSearchStore from '@/stores/GlobalSearchStore';
import { groupByTime } from '@/utils/groupByTime';
import { track } from '@/utils/track/track';

import useGlobalSearchModalAction from '../hooks/useGlobalSearchModalAction';
import SearchHistoryItem from './SearchHistoryItem';

const PAGE_SIZE = 50;

const SearchHistoryList = () => {
  const appInit = useAppInit();
  const { toDetailPage } = useGlobalSearchModalAction();
  const current = useGlobalSearchStore((state) => state.alias);
  const setHistories = useGlobalSearchStore((state) => state.setHistories);
  const histories = useGlobalSearchStore((state) => state.histories);
  const fetchSearchHistory = useCallback(async (pageParam?: string) => {
    const res = await service.globalSearch.fetchSearchHistoryList(pageParam);
    return {
      list: res,
      paginate_token: res[PAGE_SIZE - 1]?.paginate_token || null,
    };
  }, []);

  const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } =
    useInfiniteQuery({
      queryKey: queryKeys.search.SEARCH_HISTORY_LIST,
      queryFn: async ({ pageParam }) => {
        return await fetchSearchHistory(pageParam);
      },
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) => lastPage?.paginate_token,
      enabled: appInit,
    });

   
  useEffect(() => {
    if (data) {
      setHistories(data.pages.flat().flatMap((page) => page.list));
    }
  }, [data]);

  const groupedHistories = useMemo(() => {
    return groupByTime(histories, 'created_at');
  }, [histories]);

  const onSearchHistoryClick = (alias: string) => {
    track(AMP_KEYS.SEARCH_HISTORY_ITEM_CLICK);
    toDetailPage(alias);
  };

  return (
    <List
      data={groupedHistories}
      isLoading={isLoading}
      className="h-full pr-3 pb-10 gap-6"
      placeholder={{
        node: (
          <CenterFlex style={{ height: '100%' }}>
            <Typo type="label2" weight="r" className="text-gray-600">
              No saved history
            </Typo>
          </CenterFlex>
        ),
      }}
      render={(group) => {
        return (
          <VFlex key={group.title}>
            <Typo
              type="label3"
              weight="sb"
              className="text-gray-800"
              style={{
                padding: '12px',
              }}
            >
              {group.title}
            </Typo>
            {group.items.map((history) => (
              <SearchHistoryItem
                selected={current === history.alias}
                key={history.alias}
                history={history}
                onClick={onSearchHistoryClick}
              />
            ))}
          </VFlex>
        );
      }}
      isFetchingNextPage={isFetchingNextPage}
      hasNextPage={hasNextPage}
      fetchNextPage={fetchNextPage}
    />
  );
};

export default SearchHistoryList;
